import { useEffect, useRef } from "react";

// Cloudflare Turnstile widget (explicit render). Renders nothing unless a site
// key is provided, so the form works unchanged in environments where Turnstile
// is not configured (e.g. local dev or a brand without a key).

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise = null;
function loadTurnstileScript() {
  if (typeof window === "undefined") return Promise.reject();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export default function Turnstile({
  siteKey,
  onToken,
  onUnavailable,
  theme = "auto",
  className,
}) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const unavailableRef = useRef(false);

  const markUnavailable = () => {
    if (unavailableRef.current) return;
    unavailableRef.current = true;
    onUnavailable?.();
  };

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;
    unavailableRef.current = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          callback: (token) => onToken?.(token),
          "expired-callback": () => onToken?.(""),
          "error-callback": () => {
            onToken?.("");
            markUnavailable();
          },
        });
      })
      .catch(() => {
        markUnavailable();
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (_) {
          /* ignore */
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={containerRef} className={className} />;
}
